import { Component, EventEmitter, Input, NO_ERRORS_SCHEMA, OnDestroy, OnInit, Output, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import {
  DialogCloseResult,
  DialogService,
  DialogsModule,
  WindowCloseResult,
  WindowModule,
  WindowRef,
  WindowService
} from '@progress/kendo-angular-dialog';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { CheckBoxModule, InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { KENDO_PAGER, PagerModule } from '@progress/kendo-angular-pager';
import { GridModule, PageChangeEvent, PagerPosition, PagerType } from '@progress/kendo-angular-grid';
import { MulticheckFilterComponent } from '../../filters/multicheck-filter/multicheck-filter.component';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CasiCloseOptions, FormModes } from '../../common/constants.lib';
import { LoggerService } from '../../shared/logger.service';
import { MeService } from '../../auth/me.service';
import { ToastrService } from 'ngx-toastr';
import { faArrowDown, faArrowUp, faEdit, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import { setTitle } from '../../common/utils.lib';
import { ErrorDialogModalComponent } from '../../common-components/error-dialog-modal/error-dialog-modal.component';
import {
  Organisation,
  OrganisationDiscipline,
  OrganisationDisciplineNew,
  OrganisationDisciplineType
} from '../../models/models.lib';
import { CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { OrganisationDisciplineService } from '../organisation-discipline.service';
import { DeleteConfirmComponent } from '../../common-components/delete-confirm/delete-confirm.component';
import { OrganisationDisciplineAdd } from '../organisation-discipline-add/organisation-discipline-add';
import { OrganisationDisciplineUsersWin } from '../organisation-discipline-user/organisation-discipline-users-win/organisation-discipline-users-win';
import { ModalWindowService } from '../../shared/modal-window.service';

@Component({
  selector: 'casi-organisation-disciplines-win',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    CheckBoxModule,
    KENDO_PAGER,
    PagerModule,
    GridModule,
    MulticheckFilterComponent,
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './organisation-disciplines-win.html',
  styleUrl: './organisation-disciplines-win.scss'
})
export class OrganisationDisciplinesWin implements OnInit, OnDestroy {
  private _organisation: Organisation | undefined;

  @Input({ required: true }) set organisation(val: Organisation) {
    this._organisation = val;
  }
  get organisation(): Organisation | undefined {
    return this._organisation;
  };

  @Output() onCancel = new EventEmitter<void>();

  @ViewChild("appendTo", { read: ViewContainerRef }) appendTo: ViewContainerRef | undefined;
  @ViewChild("container", { read: ViewContainerRef })
  public containerRef: ViewContainerRef | undefined;

  private _windowRef: WindowRef | undefined;

  public view: Observable<OrganisationDiscipline[]> = new Observable<OrganisationDiscipline[]>();
  public rawData: OrganisationDiscipline[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: OrganisationDiscipline[] = filterBy(this.rawData, this.filter);

  public currentItem: OrganisationDisciplineType = OrganisationDisciplineNew.create(this.organisation);
  public currentSelection!: OrganisationDiscipline;

  public isSuperUser: boolean = false;

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = 10;

  public editODUsersWinOpen: Boolean = false;
  public formMode: FormModes = FormModes.NOTSET;

  public windowTop = 100;
  public windowLeft = 50;
  public isDraggable = true;

  private _showTitle = new BehaviorSubject<string>('');
  get showTitle() {
    return this._showTitle.getValue();
  }
  set showTitle(val: string) {
    this._showTitle.next(val);
  }

  private _subs: Subscription[] = [];

  constructor(
    private logger: LoggerService,
    private notificationService: NotificationService,
    private me: MeService,
    public dataService: OrganisationDisciplineService,
    public faIcons: FaIconLibrary,
    private dialogService: DialogService,
    private windowService: WindowService,
    private toastr: ToastrService,
    private modelWindowService: ModalWindowService
  ) {
    faIcons.addIcons(
      faPlusCircle, faEdit, faArrowUp, faArrowDown
    );
  }

  public sort: SortDescriptor[] = [
    { field: "discipline_name", dir: "asc" },
  ];

  //#region Ng evennt handlers

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    this.isSuperUser = this.me.get()?.superuser || false;

    if (this.organisation)
      this.dataService.query(this.organisation.id);

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      this.rawData = e;
      this.loadData();
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }

  //#endregion

  //#region Grid functions

  public pageChange(event: PageChangeEvent): void {
    this.logger.debug(`pageChange: `, event);
  }

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]);
  }

  //#endregion

  //#region Public functions

  public onCancelAction(): void {
    this.logger.debug(`onCancelAction...`);
    this.onCancel.emit();
    // this.dialog.close({ text: CasiCloseOptions.Cancelled });
  }

  onConfirmDelete(dataItem: any): void {
    this.logger.debug("onConfirmDelete: ", dataItem);
    this.currentItem = OrganisationDiscipline.create(dataItem);
    this.openConfirmDailog();
  }

  public onAddClick(): void {
    this.logger.debug("onAddClick...");
    this.currentItem = OrganisationDisciplineNew.create(this.organisation);
    this.logger.debug("onAdd:currentItem ", this.currentItem);
    this.openAddEditDialog(this.currentItem);
  }

  public onUserClick(dataItem: any): void {
    this.logger.debug("onUserClick: ", dataItem);
    let item = OrganisationDiscipline.create(dataItem);
    this.currentSelection = item;
    this.logger.debug("onUserClick:currentItem ", item);
    this.openUserDialog(item);
  }

  public close(args: any): void {
    this.logger.debug('close...', args);
    this.editODUsersWinOpen = false;
  }

  //#endregion

  //#region Private functions

  private openAddEditDialog(dataItem: OrganisationDisciplineType): void {
    this.logger.debug("openAddEditDialog...");
    const dialogRef = this.dialogService.open({
      content: OrganisationDisciplineAdd,
      minWidth: 320,
      width: 450,
    });

    const contentRef = dialogRef.content.instance as OrganisationDisciplineAdd;
    contentRef.title = 'ADD ORGANISATION DISCIPLINE';
    contentRef.organisationDiscipline = dataItem;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditDialog:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Saved) {
        this.logger.debug("openAddEditDialog:saved... ", contentRef.addForm.value);
        this.handleClose(contentRef.addForm.value);
      }
    });
  }

  private handleClose(data: any): void {
    this.logger.debug('handleClose...', data);
    if (data) {
      let inUse = this.rawData.find(r => r.discipline_id === data.discipline_id);
      this.logger.debug('handleClose...inUse', inUse);

      if (inUse) {
        this.logger.warn(`handleClose: discipline: ${data.discipline_name} already in use `);
        this.openErrorDailog("Duplicate Organisation Discipline Description", "That Organisation Discipline already exists!", "Close and Retry")
      } else
        this.save(data);
    }
  }

  private save(organisationDiscipline: any): void {
    this.logger.debug('save...', organisationDiscipline);
    this.dataService.add(organisationDiscipline)
      .then(resp => {
        this.logger.debug('save:add:resp: ', resp);
        this.notify("Organisation Discipline Added");
        this.closeWindowRef();
        if (this._organisation)
          this.dataService.query(this._organisation.id);
      })
      .catch(err => {
        this.logger.error('saveDiscipline:add:err: ', err);
        this.notify("Error Creating Organisation Discipline", true);
      });
  }

  private setWinInstanceInputs(dataItem: Organisation): void {
    if (this._windowRef) {
      const inputs = this._windowRef.content.instance;
      inputs.organisationDiscipline = dataItem;
      this.logger.debug('setWinInstanceInputs: ', inputs);
    }
  }

  private openUserDialog(dataItem: OrganisationDiscipline): void {
    this.logger.debug("openUserDialog...");
    this.closeWindowRef();
    this._windowRef = this.modelWindowService.open({
      title: 'MANAGE ORGANISATION DISCIPLINE USERS',
      content: OrganisationDisciplineUsersWin,
      minWidth: 320,
      width: 800,
      modal: true,
    });

    this.setWinInstanceInputs(dataItem);

    this._windowRef.result.subscribe((r: any) => {
      this.logger.debug('openSubscaleDialog:result:', r);
      // if (r instanceof DialogCloseResult) {
      if (r instanceof WindowCloseResult) {
        this.closeWindowRef();
        if (this._organisation)
          this.dataService.query(this._organisation.id);
      }
    });

    this._windowRef.content.instance.onCancel.subscribe(() => {
      this.logger.debug('openSubscaleDialog:onCancel');
      this.closeWindowRef();
    });

  }

  private openConfirmDailog(): void {
    this.logger.debug("openConfirmDailog: ", this.currentItem);
    const dialogRef = this.dialogService.open({
      content: DeleteConfirmComponent,
      minWidth: 250,
      width: 450
    });

    const contentRef = dialogRef.content.instance as DeleteConfirmComponent;
    contentRef.title = "CONFIRM DELETE";
    contentRef.content = `Delete Discipline - <b>${this.currentItem?.discipline_name}</b>?`;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openConfirmDailog:ok to proceed... ", this.currentItem);
        this.destroy(this.currentItem);
      }
    });
  }

  private destroy(organisationDiscipline: any): void {
    this.logger.debug('save...', organisationDiscipline);
    this.dataService.destroy(organisationDiscipline.id)
      .then(resp => {
        this.logger.debug('save:update:resp: ', resp);
        this.notify("Organisation Discipline Deleted");
        this.closeWindowRef();
        if (this._organisation)
          this.dataService.query(this._organisation.id);
      })
      .catch(err => {
        this.logger.error('saveDiscipline:update:err: ', err);
        this.notify("Error Deleting Organisation Discipline", true);
      });
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private openErrorDailog(title: string = 'Not set', content: string = 'No content', buttonText: string = 'Close'): void {
    const dialogRef = this.dialogService.open({
      content: ErrorDialogModalComponent,
      minWidth: 320,
      width: 750,
      cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as ErrorDialogModalComponent;
    contentRef.title = title;
    contentRef.content = content;
    contentRef.buttonText = buttonText;
  }

  private closeWindowRef(): void {
    this.logger.debug(`closeWindowRef...`);
    if (this._windowRef) {
      this.modelWindowService.close(this._windowRef);
      this._windowRef = undefined;
    }
    this.editODUsersWinOpen = false;
  }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }

  //#endregion

}

